import { TitleButtonDivider } from '@/common/buttons/TitleButtonDivider';
import { EntityPerspective } from '@/common/entityFramework/entityPerspective/EntityPerspective';
import { useFilterButtons } from '@/common/entityFramework/entityPerspective/hooks/useFilterButtons';
import { RowIcon } from '@/common/entityFramework/entityPerspective/support/RowIcon';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { useCallback, useRef } from 'react';
import { TheraSelect } from '../../theraSelect/TheraSelect';

export const AbvReservePerspective = () => {
  const entityPerspectiveRef = useRef();
  const theraSelectRef = useRef();

  const views = [
    {
      name: 'Balances',
      dims: ['PAID_PERIOD', 'CHANNEL', 'PROD', 'EARNED_PERIOD'],
      kpis: [
        'START_BASE_BAL',
        'START_PIPE_BAL',
        'START_BAL',
        'ACCR_AMT',
        'BS_ACCR_ADJ',
        'PL_ACCR_ADJ',
        'BS_PIPE_ADJ',
        'PL_PIPE_ADJ',
        'TOTAL_ACCR',
        'PYMT_AMT',
        'PYMT_ADJ',
        'TOTAL_PYMT',
        'END_BASE_BAL',
        'END_PIPE_BAL',
        'END_BAL',
      ],
      numFrozenDims: 1,
      dimToPeriodicities: {
        PAID_PERIOD: ['LIFETIME', 'YEAR', 'QTR', 'MON'],
        EARNED_PERIOD: ['YEAR', 'QTR'],
      },
    },

    {
      name: 'Aging',
      dims: ['EARNED_PERIOD', 'PAID_PERIOD', 'CHANNEL', 'PROD'],
      kpis: [
        'START_BASE_BAL',
        'ACCR_AMT',
        'BS_ACCR_ADJ',
        'PL_ACCR_ADJ',
        'TOTAL_BASE_ACCR',
        'PYMT_AMT',
        'PYMT_ADJ',
        'TOTAL_PYMT',
        'END_BASE_BAL',
      ],
      numFrozenDims: 1,
      dimToPeriodicities: {
        PAID_PERIOD: ['YEAR', 'QTR', 'MON'],
        EARNED_PERIOD: ['YEAR', 'QTR'],
      },
    },
  ];

  const { filterButtonStages, filterButtons, onViewReset } = useFilterButtons([
    {
      key: 'negEndBal',
      icon: fas.faExclamationTriangle,
      colors: [null, 'red', 'green'],
      toolTips: ['Positive and negative end balance', 'Negative end balance', 'Positive end balance'],
    },
  ]);

  const augButtons = (buttons) => {
    buttons.push(
      <TheraSelect entityPerspectiveRef={entityPerspectiveRef} ref={theraSelectRef} />,
      ...filterButtons,
      <TitleButtonDivider key="filterButtonDivider" />,
    );
  };

  const augReqParam = useCallback(
    (reqParam) => {
      // Negative end balance
      const negEndBalButtonState = filterButtonStages.negEndBal;
      if (negEndBalButtonState > 0) {
        reqParam.expr = ExprUtils.and(
          reqParam.expr,
          negEndBalButtonState === 1 ? ExprUtils.whereParams('negBal', 1) : ExprUtils.isNot('negBal', 1),
        );
      }
    },
    [filterButtonStages],
  );

  const rowIconsFn = (data) => {
    let rowIcons = [];

    if (data.closed) {
      rowIcons.push(<RowIcon key="closed" icon={fas.faCheckCircle} color="green" toolTip="Period is closed" />);
    }

    if (data.negBal === 1) {
      rowIcons.push(
        <RowIcon key="negEndBal" icon={fas.faExclamationTriangle} color="red" toolTip="End balance is negative" />,
      );
    } else if (data.negBal > 0) {
      rowIcons.push(
        <RowIcon
          key="containsNegEndBal"
          icon={fas.faExclamationTriangle}
          color="#FFC4C8"
          toolTip="Contains an end balance that is negative"
        />,
      );
    }

    return rowIcons;
  };

  const getContextMenuItems = (params) => {
    let contextMenus = entityPerspectiveRef.current.getContextMenuItems(params);

    // Close Period Context Menu
    if (!params.node.data.closed && params.node.data.dim === 'PAID_PERIOD') {
      contextMenus.push({
        name: 'Close Period',
        icon: fas.faCheckCircle,
        action: () => entityPerspectiveRef.current.openActionDialog(params, 'Close this Period', 'CLOSE_ACTION_PLUGIN'),
      });
    }

    // Overwrite Balance Context Menu
    if (
      !params.node.data.closed &&
      (params.node.data.node.includes('DSA') || params.node.data.node.includes('Bad Debt'))
    ) {
      contextMenus.push(
        {
          name: 'Overwrite Balance',
          icon: fas.faPencil,
          action: () =>
            entityPerspectiveRef.current.openActionDialog(
              params,
              'Overwrite Balance',
              'OVERWRITE_BALANCE_ACTION_PLUGIN',
            ),
        },
        {
          name: 'Adjustment Details',
          action: () =>
            entityPerspectiveRef.current.navigateToPerspectiveAdj(params, 'ACCR_AMT', 'Balance Adjustment Details'),
          icon: fas.faListUl,
        },
        {
          name: 'Export',
          action: () => entityPerspectiveRef.current.openExportDialog(params),
          icon: fas.faArrowCircleDown,
        },
      );

      return contextMenus;
    }

    let accrSubMenu = [
      {
        name: 'Accrual Amounts',
        action: () => entityPerspectiveRef.current.navigateToViewer(params, 'ACCR_AMT', 'Accrual Amounts'),
        icon: fas.faListUl,
      },
      {
        name: 'Pivot Accrual Amounts',
        action: () => entityPerspectiveRef.current.navigateToPivot(params, 'ACCR_AMT', 'Pivot Accrual Amounts'),
        icon: fas.faTableColumns,
      },
      {
        name: 'BS Accrual Adjustments',
        action: () =>
          entityPerspectiveRef.current.navigateToEntityList(params, 'MAN_ACCR_ADJ', manAccrAdjNavState('BS_ACCR_ADJ')),
        icon: fas.faListUl,
      },
      {
        name: 'P/L Accrual Adjustments',
        action: () =>
          entityPerspectiveRef.current.navigateToEntityList(params, 'MAN_ACCR_ADJ', manAccrAdjNavState('PL_ACCR_ADJ')),
        icon: fas.faListUl,
      },
      {
        name: 'BS Pipeline Adjustments',
        action: () =>
          entityPerspectiveRef.current.navigateToEntityList(params, 'MAN_ACCR_ADJ', manAccrAdjNavState('BS_PIPE_ADJ')),
        icon: fas.faListUl,
      },
      {
        name: 'P/L Pipeline Adjustments',
        action: () =>
          entityPerspectiveRef.current.navigateToEntityList(params, 'MAN_ACCR_ADJ', manAccrAdjNavState('PL_PIPE_ADJ')),
        icon: fas.faListUl,
      },
    ];

    let pymtSubMenu = [
      {
        name: 'Payment Amounts',
        action: () => entityPerspectiveRef.current.navigateToViewer(params, 'PYMT_AMT', 'Payment Amounts'),
        icon: fas.faListUl,
      },
      {
        name: 'Pivot Payment Amounts',
        action: () => entityPerspectiveRef.current.navigateToPivot(params, 'PYMT_AMT', 'Pivot Payment Amounts'),
        icon: fas.faTableColumns,
      },
      {
        name: 'Payment Adjustments',
        action: () => entityPerspectiveRef.current.navigateToViewer(params, 'PYMT_ADJ', 'Payment Adjustments'),
        icon: fas.faListUl,
      },
      {
        name: 'Pivot Payment Adjustments',
        action: () => entityPerspectiveRef.current.navigateToPivot(params, 'PYMT_ADJ', 'Pivot Payment Adjustments'),
        icon: fas.faTableColumns,
      },
    ];

    contextMenus.push(
      {
        name: 'Accruals',
        subMenu: accrSubMenu,
        icon: fas.faListUl,
      },
      {
        name: 'Payments',
        subMenu: pymtSubMenu,
        icon: fas.faListUl,
      },
      {
        name: 'Export',
        action: () => entityPerspectiveRef.current.openExportDialog(params),
        icon: fas.faArrowCircleDown,
      },
    );

    return contextMenus;
  };

  const manAccrAdjNavState = (trxGroup) => {
    const manAccrAdjNavState = {
      reqParam: { trxGroup },
      pluginCode: 'MAN_ACCR_ADJ_RESERVE_DATA_PLUGIN',
    };

    return manAccrAdjNavState;
  };

  const handleViewReset = useCallback(() => {
    onViewReset();
    theraSelectRef.current.reset();
  }, [onViewReset]);

  const events = {
    onDimFilterChange: ({ dimCode }) => {
      if (dimCode === 'PROD') {
        theraSelectRef.current.reset();
      }
    },
  };

  return (
    <EntityPerspective
      ref={entityPerspectiveRef}
      views={views}
      augButtons={augButtons}
      augReqParam={augReqParam}
      rowIconsFn={rowIconsFn}
      onViewReset={handleViewReset}
      events={events}
      gridOptions={{
        getContextMenuItems,
      }}
    />
  );
};
